import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import {
  User,
  LoginCredentials,
  LoginResponse,
  LogoutResponse,
} from '../models';
import { ENDPOINTS_AUTH } from '../constants';
import { USER_ROLES } from '../constants/roles.constant';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly httpClient = inject(HttpClient);
  private readonly router = inject(Router);

  // Core state signals
  private readonly userSignal = signal<User | null>(null);
  private readonly tokenSignal = signal<string | null>(null);
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);

  // Computed derived state
  isAuthenticated = computed(() => !!this.userSignal() && !!this.tokenSignal());
  currentUser = computed(() => this.userSignal());
  isLoading = computed(() => this.loadingSignal());
  error = computed(() => this.errorSignal());
  /** Configuration management is Admin-only; preserve the super-admin bypass. */
  canManageEstimationConfiguration = computed(
    () =>
      this.userSignal()?.is_super === true ||
      (this.userSignal()?.roles ?? []).some(
        role => role.name === USER_ROLES.ADMIN
      )
  );

  constructor() {
    // Check for existing session on service initialization
    this.checkExistingSession();
  }

  /**
   * Login with email and password using API
   */
  login(credentials: LoginCredentials): Observable<LoginResponse> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    return this.httpClient
      .post<LoginResponse>(ENDPOINTS_AUTH.POST_LOGIN, credentials)
      .pipe(
        tap(response => {
          if (response?.data?.admin && response?.data?.accessToken) {
            // Set user and token, persist to localStorage
            this.userSignal.set(response.data.admin);
            this.tokenSignal.set(response.data.accessToken);
            localStorage.setItem(
              'arm_user',
              JSON.stringify(response.data.admin)
            );
            localStorage.setItem('arm_token', response.data.accessToken);
            this.loadingSignal.set(false);
          } else {
            const errorMsg = 'Login failed - invalid response format';
            this.errorSignal.set(errorMsg);
            this.loadingSignal.set(false);
          }
        }),
        catchError(error => {
          this.errorSignal.set('Login failed. Please try again.');
          this.loadingSignal.set(false);
          return throwError(() => error);
        })
      );
  }

  /**
   * Logout current user using API
   */
  logout(): Observable<LogoutResponse> {
    this.loadingSignal.set(true);
    const token = this.tokenSignal();

    if (!token) {
      this.clearUserData();
      return of({ success: true, message: 'Logged out locally' });
    }

    return this.httpClient
      .post<LogoutResponse>(ENDPOINTS_AUTH.POST_LOGOUT, {})
      .pipe(
        tap(() => {
          this.clearUserData();
        }),
        catchError(() => {
          this.clearUserData();
          return of({ success: true, message: 'Logged out locally' });
        })
      );
  }

  /**
   * Clear user data and navigate to login
   */
  private clearUserData(): void {
    this.userSignal.set(null);
    this.tokenSignal.set(null);
    localStorage.removeItem('arm_user');
    localStorage.removeItem('arm_token');
    this.loadingSignal.set(false);
    this.router.navigate(['/login']);
  }

  /**
   * Check if user is super admin
   */
  isSuperAdmin(): boolean {
    const user = this.userSignal();
    return user?.is_super === true;
  }

  /**
   * Whether the signed-in admin holds a permission.
   *
   * A super admin bypasses the check, mirroring the backend gate. Permissions
   * are read from the login payload, so a permission granted after sign-in only
   * takes effect on the next sign-in.
   */
  hasPermission(permission: string): boolean {
    if (this.isSuperAdmin()) {
      return true;
    }

    return (this.userSignal()?.permissions ?? []).some(
      granted => granted.name === permission
    );
  }

  /**
   * Whether the signed-in admin holds at least one of the permissions.
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Check for existing session in localStorage
   */
  private checkExistingSession(): void {
    try {
      const storedUser = localStorage.getItem('arm_user');
      const storedToken = localStorage.getItem('arm_token');

      if (storedUser && storedToken) {
        const user = JSON.parse(storedUser) as User;
        this.userSignal.set(user);
        this.tokenSignal.set(storedToken);
      }
    } catch {
      // Clear invalid session data
      localStorage.removeItem('arm_user');
      localStorage.removeItem('arm_token');
    }
  }

  /**
   * Get authentication token for API requests
   */
  getToken(): string | null {
    return this.tokenSignal();
  }
}
